import { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import { filterModeAtom } from '@src/atoms/page';
import { formatColumns, formatAppData, formatMultiColumns, formatMultiData } from '../../../utils';
import { getPercentageMetric } from '@utils/metrics/metricHelpers';
import useFetchGroupBy from '../../../../hooks/useFetchGroupBy';
import { useAppCommonQueryParams } from '../../../../hooks/useAppDimension';

const getUnknownName = (dataItem: Record<string, any>) => {
  if (Array.isArray(dataItem.name)) {
    return dataItem.name[0];
  }
  return dataItem.name;
};

export const useAppMultiDimensions = ({ dimensions, unit, enabled = true }) => {
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);

  const {
    metrics,
    filter,
    interval,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    selectedMetric,
  } = useAppCommonQueryParams();

  const appFilterMode = useAtomValue(filterModeAtom);
  const dimensionByTypes: any = useSelector(dimensionByTypesSelector);

  const [sortBy, setSortBy] = useState({ key: sortByMetric, order: 'desc' as const });

  // update local sort when selected or order by metric changes
  useEffect(() => {
    setSortBy({ key: sortByMetric, order: 'desc' });
  }, [sortByMetric, selectedMetric]);

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension: dimensions,
    metrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
  });

  const { adjustedInterval }: any = data;

  const { rows, totals, columns, pctShareMaxValues } = useMemo(() => {
    let rows = [];
    let totals = {};
    let columns = [];
    let pctShareMaxValues = {};

    // unit based metrics
    const columnMetrics = metrics.map((metric) => {
      return unit === '%' ? getPercentageMetric(metric) : metric;
    });

    const currentDimensionType = dimensionByTypes[dimensions[0]];

    if (!isEmpty(dimensionByTypes)) {
      // Need to test metric filter data case
      if ('metricFilterData' in data) {
        ({ rows, totals, pctShareMaxValues } = formatMultiData({
          data,
          sortBy,
          metrics: columnMetrics,
          getUnknownName,
        }));
        columns = formatMultiColumns({
          dimension: dimensions,
          metrics: columnMetrics,
          showNAForMetric: null,
          realTimeOnTrends,
        });
      } else {
        ({ rows, totals, pctShareMaxValues } = formatAppData({
          data,
          sortBy,
          appFilterMode,
          columnMetrics,
          currentDimensionType,
          getUnknownName,
        }));
        columns = formatColumns({
          dimension: dimensions,
          metrics: columnMetrics,
          showNAForMetric: null,
          realTimeOnTrends,
        });
      }
    }

    return { rows, totals, columns, pctShareMaxValues };
  }, [
    appFilterMode,
    data,
    dimensions,
    dimensionByTypes,
    metrics,
    currentMetricsMeta,
    realTimeOnTrends,
    sortBy,
    unit,
  ]);

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    adjustedInterval,
    pctShareMaxValues,
    metrics,
  };
};
